import { useState, useEffect, useRef } from "react";

interface AIOrbAvatarProps {
  size?: number;
  className?: string;
  showEyes?: boolean;
  speed?: number;
  staticCircle?: boolean; // For message avatar: fixed pure circle, zero blur, zero drop-shadow
}

// 12-Segment minimal-vertex 100% mathematically symmetric cubic bezier paths (Extra Plump, Extra Heavy roundness on ALL shapes)
const PATH_CIRCLE =
  'M 174.00 100.00 C 174.00 112.92, 170.54 125.81, 164.09 137.00 C 157.63 148.19, 148.19 157.63, 137.00 164.09 C 125.81 170.54, 112.92 174.00, 100.00 174.00 C 87.08 174.00, 74.19 170.54, 63.00 164.09 C 51.81 157.63, 42.37 148.19, 35.91 137.00 C 29.46 125.81, 26.00 112.92, 26.00 100.00 C 26.00 87.08, 29.46 74.19, 35.91 63.00 C 42.37 51.81, 51.81 42.37, 63.00 35.91 C 74.19 29.46, 87.08 26.00, 100.00 26.00 C 112.92 26.00, 125.81 29.46, 137.00 35.91 C 148.19 42.37, 157.63 51.81, 164.09 63.00 C 170.54 74.19, 174.00 87.08, 174.00 100.00 Z';

// Extra Plump & Rounded 4-Point Sparkle / Star (Thick rounded waist so eyes stay comfortably centered)
const PATH_STAR_4 =
  'M 187.00 100.00 C 187.00 115.18, 171.15 127.48, 158.46 133.75 C 145.76 140.02, 140.02 145.76, 133.75 158.46 C 127.48 171.15, 115.18 187.00, 100.00 187.00 C 84.82 187.00, 72.52 171.15, 66.25 158.46 C 59.98 145.76, 54.24 140.02, 41.54 133.75 C 28.85 127.48, 13.00 115.18, 13.00 100.00 C 13.00 84.82, 28.85 72.52, 41.54 66.25 C 54.24 59.98, 59.98 54.24, 66.25 41.54 C 72.52 28.85, 84.82 13.00, 100.00 13.00 C 115.18 13.00, 127.48 28.85, 133.75 41.54 C 140.02 54.24, 145.76 59.98, 158.46 66.25 C 171.15 72.52, 187.00 84.82, 187.00 100.00 Z';

// Extra Soft 4-Lobe Clover / Pebble
const PATH_CLOVER_4 =
  'M 163.00 100.00 C 163.00 111.00, 170.03 124.41, 168.85 139.75 C 167.67 155.09, 155.09 167.67, 139.75 168.85 C 124.41 170.03, 111.00 163.00, 100.00 163.00 C 89.00 163.00, 75.59 170.03, 60.25 168.85 C 44.91 167.67, 32.33 155.09, 31.15 139.75 C 29.97 124.41, 37.00 111.00, 37.00 100.00 C 37.00 89.00, 29.97 75.59, 31.15 60.25 C 32.33 44.91, 44.91 32.33, 60.25 31.15 C 75.59 29.97, 89.00 37.00, 100.00 37.00 C 111.00 37.00, 124.41 29.97, 139.75 31.15 C 155.09 32.33, 167.67 44.91, 168.85 60.25 C 170.03 75.59, 163.00 89.00, 163.00 100.00 Z';

// Extra Soft 6-Petal Flower
const PATH_FLOWER_6 =
  'M 183.00 100.00 C 183.00 114.49, 161.96 122.68, 156.29 132.50 C 150.62 142.32, 154.05 164.64, 141.50 171.88 C 128.95 179.12, 111.34 165.00, 100.00 165.00 C 88.66 165.00, 71.05 179.12, 58.50 171.88 C 45.95 164.64, 49.38 142.32, 43.71 132.50 C 38.04 122.68, 17.00 114.49, 17.00 100.00 C 17.00 85.51, 38.04 77.32, 43.71 67.50 C 49.38 57.68, 45.95 35.36, 58.50 28.12 C 71.05 20.88, 88.66 35.00, 100.00 35.00 C 111.34 35.00, 128.95 20.88, 141.50 28.12 C 154.05 35.36, 150.62 57.68, 156.29 67.50 C 161.96 77.32, 183.00 85.51, 183.00 100.00 Z';

// Extra Heavy-Rounded Cushion Squircle
const PATH_SQUIRCLE =
  'M 182.00 100.00 C 182.00 114.31, 170.92 126.84, 160.62 135.00 C 150.33 143.16, 143.16 150.33, 135.00 160.62 C 126.84 170.92, 114.31 182.00, 100.00 182.00 C 85.69 182.00, 73.16 170.92, 65.00 160.62 C 56.84 150.33, 49.67 143.16, 39.38 135.00 C 29.08 126.84, 18.00 114.31, 18.00 100.00 C 18.00 85.69, 29.08 73.16, 39.38 65.00 C 49.67 56.84, 56.84 49.67, 65.00 39.38 C 73.16 29.08, 85.69 18.00, 100.00 18.00 C 114.31 18.00, 126.84 29.08, 135.00 39.38 C 143.16 49.67, 150.33 56.84, 160.62 65.00 C 170.92 73.16, 182.00 85.69, 182.00 100.00 Z';

const MORPH_VALUES = `${PATH_CIRCLE}; ${PATH_STAR_4}; ${PATH_CLOVER_4}; ${PATH_FLOWER_6}; ${PATH_SQUIRCLE}; ${PATH_CIRCLE}`;
const MORPH_DUR = '48s';
const KEY_TIMES = '0; 0.2; 0.4; 0.6; 0.8; 1';
const KEY_SPLINES = '0.33 0 0.2 1; 0.33 0 0.2 1; 0.33 0 0.2 1; 0.33 0 0.2 1; 0.33 0 0.2 1';

export function AIOrbAvatar({
  size = 132,
  className = '',
  showEyes = true,
  staticCircle = false,
}: AIOrbAvatarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const eyePosRef = useRef({ currentX: 0, currentY: 0, targetX: 0, targetY: 0 });
  const [eyeOffset, setEyeOffset] = useState({ x: 0, y: 0 });
  const [lightOffset, setLightOffset] = useState({ x: 0, y: 0 });

  // Ultra-responsive, smooth omnidirectional mouse tracking loop (360 degrees: Up, Down, Left, Right)
  useEffect(() => {
    let animFrameId: number;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const deltaX = e.clientX - centerX;
      const deltaY = e.clientY - centerY;
      const distance = Math.hypot(deltaX, deltaY);
      if (distance === 0) return;

      const angle = Math.atan2(deltaY, deltaX);
      const maxRange = 500;
      const strength = Math.min(distance / maxRange, 1);
      const easeFactor = Math.pow(strength, 0.65);

      // Safe, balanced travel distance in all 360 directions
      const maxTravel = staticCircle ? 8 : 15;

      eyePosRef.current.targetX = Math.cos(angle) * maxTravel * easeFactor;
      eyePosRef.current.targetY = Math.sin(angle) * maxTravel * easeFactor;
    };

    // Smooth LERP animation loop with buttery 0.07 dampening factor
    const updateMotion = () => {
      const { currentX, currentY, targetX, targetY } = eyePosRef.current;

      const newX = currentX + (targetX - currentX) * 0.07;
      const newY = currentY + (targetY - currentY) * 0.07;

      eyePosRef.current.currentX = newX;
      eyePosRef.current.currentY = newY;

      setEyeOffset({ x: Number(newX.toFixed(2)), y: Number(newY.toFixed(2)) });
      setLightOffset({ x: Number((newX * 0.45).toFixed(2)), y: Number((newY * 0.45).toFixed(2)) });

      animFrameId = requestAnimationFrame(updateMotion);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    animFrameId = requestAnimationFrame(updateMotion);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animFrameId);
    };
  }, [staticCircle]);

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative flex items-center justify-center select-none ${
        !staticCircle ? 'cursor-pointer transition-transform duration-500 ease-out' : ''
      } ${
        !staticCircle && isHovered ? 'scale-[1.06] -translate-y-1' : 'scale-100 translate-y-0'
      } ${className}`}
      style={{ width: size, height: size }}
    >
      {/* Soft Ethereal Ambient Underglow (Rendered only on hero avatar, hidden on message avatar) */}
      {!staticCircle && (
        <div
          className={`absolute inset-0 rounded-full pointer-events-none transition-all duration-700 ease-out ${
            isHovered
              ? 'opacity-85 scale-[1.2] blur-[24px]'
              : 'opacity-65 scale-100 blur-[18px]'
          }`}
          style={{
            background:
              'radial-gradient(circle, rgba(167, 139, 250, 0.45) 0%, rgba(125, 211, 252, 0.35) 45%, rgba(192, 132, 252, 0.2) 75%, transparent 100%)',
            transformOrigin: 'center center',
          }}
        />
      )}

      {/* SVG Vector Body */}
      <svg
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`w-full h-full overflow-visible ${
          staticCircle
            ? ''
            : `transition-all duration-500 ease-out ${
                isHovered
                  ? 'drop-shadow-[0_14px_32px_rgba(147,112,219,0.3)]'
                  : 'drop-shadow-[0_10px_24px_rgba(147,112,219,0.18)]'
              }`
        }`}
      >
        <defs>
          {/* Subtle Diffused Filters */}
          <filter id={`soft-ambient-underglow-${staticCircle ? 'static' : 'hero'}`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation={staticCircle ? "4" : "12"} />
          </filter>

          <filter id={`deep-inner-blur-${staticCircle ? 'static' : 'hero'}`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation={staticCircle ? "6" : "10"} result="blur" />
          </filter>

          <filter id={`soft-flare-blur-${staticCircle ? 'static' : 'hero'}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceGraphic" stdDeviation={staticCircle ? "3" : "6"} />
          </filter>

          <filter id={`feathered-rim-blur-${staticCircle ? 'static' : 'hero'}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceGraphic" stdDeviation={staticCircle ? "1.2" : "2.5"} />
          </filter>

          <filter id={`eye-glow-${staticCircle ? 'static' : 'hero'}`} x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="0" stdDeviation="2.5" floodColor="#FFFFFF" floodOpacity="0.9" />
            <feDropShadow dx="0" dy="2" stdDeviation="3.5" floodColor="#818CF8" floodOpacity="0.45" />
          </filter>

          {/* Base Radial Gradient - Soft Lavender-Tinted White, Pastel Lavender & Sky-Blue */}
          <radialGradient
            id={`soothing-pastel-radial-${staticCircle ? 'static' : 'hero'}`}
            cx="42%"
            cy="32%"
            r="84%"
            fx="38%"
            fy="28%"
          >
            <stop offset="0%" stopColor="#FAF5FF" stopOpacity="0.95">
              {!staticCircle && (
                <animate
                  attributeName="stop-color"
                  values="#FAF5FF;#F5F3FF;#EDE9FE;#FAF5FF"
                  dur="20s"
                  repeatCount="indefinite"
                />
              )}
            </stop>
            <stop offset="28%" stopColor="#F3E8FF" stopOpacity="0.9">
              {!staticCircle && (
                <animate
                  attributeName="stop-color"
                  values="#F3E8FF;#E0F2FE;#F5F3FF;#F3E8FF"
                  dur="20s"
                  repeatCount="indefinite"
                />
              )}
            </stop>
            <stop offset="55%" stopColor="#C4B5FD" stopOpacity="0.8">
              {!staticCircle && (
                <animate
                  attributeName="stop-color"
                  values="#C4B5FD;#BAE6FD;#DDD6FE;#C4B5FD"
                  dur="20s"
                  repeatCount="indefinite"
                />
              )}
            </stop>
            <stop offset="80%" stopColor="#7DD3FC" stopOpacity="0.7">
              {!staticCircle && (
                <animate
                  attributeName="stop-color"
                  values="#7DD3FC;#A78BFA;#38BDF8;#7DD3FC"
                  dur="20s"
                  repeatCount="indefinite"
                />
              )}
            </stop>
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0.5">
              {!staticCircle && (
                <animate
                  attributeName="stop-color"
                  values="#6366F1;#818CF8;#4F46E5;#6366F1"
                  dur="20s"
                  repeatCount="indefinite"
                />
              )}
            </stop>
          </radialGradient>

          {/* Soft Lavender & Light Sky-Blue Fluid Stream */}
          <linearGradient id={`soothing-blue-lavender-stream-${staticCircle ? 'static' : 'hero'}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7DD3FC" stopOpacity="0.75" />
            <stop offset="50%" stopColor="#A78BFA" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#60A5FA" stopOpacity="0.65" />
          </linearGradient>

          {/* Clip Path: Static Circle or Morphing Path */}
          <clipPath id={`morphing-avatar-clip-${staticCircle ? 'static' : 'hero'}`}>
            <path d={PATH_CIRCLE}>
              {!staticCircle && (
                <animate
                  attributeName="d"
                  values={MORPH_VALUES}
                  dur={MORPH_DUR}
                  calcMode="spline"
                  keyTimes={KEY_TIMES}
                  keySplines={KEY_SPLINES}
                  repeatCount="indefinite"
                />
              )}
            </path>
          </clipPath>
        </defs>

        {/* Outer Body Layer (Slow gentle 360 rotation) */}
        <g className={staticCircle ? '' : 'continuous-slow-rotate'}>
          {/* Ambient Soft Underglow (Only for hero avatar) */}
          {!staticCircle && (
            <path
              d={PATH_CIRCLE}
              fill="rgba(167, 139, 250, 0.4)"
              filter={`url(#soft-ambient-underglow-${staticCircle ? 'static' : 'hero'})`}
              transform="scale(1.05) translate(-5, -3)"
              style={{ transformOrigin: 'center center' }}
            >
              <animate
                attributeName="d"
                values={MORPH_VALUES}
                dur={MORPH_DUR}
                calcMode="spline"
                keyTimes={KEY_TIMES}
                keySplines={KEY_SPLINES}
                repeatCount="indefinite"
              />
            </path>
          )}

          {/* Base Fill Layer */}
          <g clipPath={`url(#morphing-avatar-clip-${staticCircle ? 'static' : 'hero'})`}>
            {/* Base Soothing Pastel Gradient */}
            <rect width="200" height="200" fill={`url(#soothing-pastel-radial-${staticCircle ? 'static' : 'hero'})`} />

            {/* Interactive Parallax Fluid Light Blobs */}
            <g transform={`translate(${lightOffset.x}, ${lightOffset.y})`}>
              {/* Swirling Soft Lavender/Light Blue Stream */}
              <ellipse
                cx="130"
                cy="88"
                rx="45"
                ry="40"
                fill={`url(#soothing-blue-lavender-stream-${staticCircle ? 'static' : 'hero'})`}
                filter={`url(#deep-inner-blur-${staticCircle ? 'static' : 'hero'})`}
                opacity={0.85}
              />

              {/* Gentle Center Soft Glow */}
              <ellipse
                cx="86"
                cy="76"
                rx="42"
                ry="32"
                fill="#FAF5FF"
                filter={`url(#soft-flare-blur-${staticCircle ? 'static' : 'hero'})`}
                opacity={0.82}
              />

              {/* Delicate Lavender Ambient Highlight */}
              <circle
                cx="118"
                cy="118"
                r="36"
                fill="rgba(167, 139, 250, 0.35)"
                filter={`url(#deep-inner-blur-${staticCircle ? 'static' : 'hero'})`}
              />
            </g>

            {/* 3D Glass Specular Curved Highlight */}
            <path
              d="M 54 48 A 76 76 0 0 1 146 48 A 72 72 0 0 0 54 48 Z"
              fill="#FAF5FF"
              fillOpacity={0.4}
              filter={`url(#soft-flare-blur-${staticCircle ? 'static' : 'hero'})`}
            />
          </g>

          {/* Clean Border Rim */}
          <path
            d={PATH_CIRCLE}
            fill="none"
            stroke="rgba(255, 255, 255, 0.8)"
            strokeWidth={staticCircle ? "2" : "2.8"}
            filter={staticCircle ? undefined : `url(#feathered-rim-blur-${staticCircle ? 'static' : 'hero'})`}
            opacity={0.85}
          >
            {!staticCircle && (
              <animate
                attributeName="d"
                values={MORPH_VALUES}
                dur={MORPH_DUR}
                calcMode="spline"
                keyTimes={KEY_TIMES}
                keySplines={KEY_SPLINES}
                repeatCount="indefinite"
              />
            )}
          </path>
        </g>

        {/* AI Eyes Layer (Positioned with slightly elevated, expressive y=76, follows mouse 360°) */}
        {showEyes && (
          <g transform={`translate(${eyeOffset.x}, ${eyeOffset.y})`}>
            {/* Left Eye */}
            <rect
              x="83"
              y="76"
              width="11"
              height="32"
              rx="5.5"
              fill="white"
              filter={`url(#eye-glow-${staticCircle ? 'static' : 'hero'})`}
            >
              <animate
                attributeName="height"
                values="32;32;2;32;32"
                keyTimes="0;0.88;0.92;0.96;1"
                dur="4.5s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="y"
                values="76;76;91;76;76"
                keyTimes="0;0.88;0.92;0.96;1"
                dur="4.5s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="rx"
                values="5.5;5.5;1;5.5;5.5"
                keyTimes="0;0.88;0.92;0.96;1"
                dur="4.5s"
                repeatCount="indefinite"
              />
            </rect>

            {/* Right Eye */}
            <rect
              x="106"
              y="76"
              width="11"
              height="32"
              rx="5.5"
              fill="white"
              filter={`url(#eye-glow-${staticCircle ? 'static' : 'hero'})`}
            >
              <animate
                attributeName="height"
                values="32;32;2;32;32"
                keyTimes="0;0.88;0.92;0.96;1"
                dur="4.5s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="y"
                values="76;76;91;76;76"
                keyTimes="0;0.88;0.92;0.96;1"
                dur="4.5s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="rx"
                values="5.5;5.5;1;5.5;5.5"
                keyTimes="0;0.88;0.92;0.96;1"
                dur="4.5s"
                repeatCount="indefinite"
              />
            </rect>
          </g>
        )}
      </svg>
    </div>
  );
}
